import { z } from 'zod';
import {
  PAGINATION_PAGE_SIZE_LARGE,
  PAGINATION_SORT_DIRECTION,
} from '~/constants';

import { EmailSchema, toSnakeCase } from '@vestigas/utils';

import { USER_ANALYTICS_DETAILS_SORT_BY } from './constants';

/**
 * Zod schema for validating user analytics details query parameters.
 */
export const zodUserAnalyticsDetailsQueryParamsSchema = z
  .object({
    dateRange: z.object({
      dateFrom: z.iso.date(),
      dateTo: z.iso.date(),
    }),
    limit: z
      .number()
      .int()
      .min(1)
      .max(500)
      .prefault(PAGINATION_PAGE_SIZE_LARGE),
    offset: z.number().int().nonnegative().prefault(0),
    page: z.number().int().nonnegative().optional(),
    searchString: z.string().trim().optional().prefault(''),
    sortBy: z
      .preprocess(
        (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
        z.enum(USER_ANALYTICS_DETAILS_SORT_BY),
      )
      .optional()
      .prefault('first_name')
      .catch('first_name'),
    sortOrder: z
      .preprocess(
        (value) => String(value).toUpperCase(),
        z.enum(PAGINATION_SORT_DIRECTION),
      )
      .optional()
      .prefault('DESC'),
  })
  .transform(({ limit, offset, page, ...rest }) => {
    return {
      ...rest,
      limit,
      offset: page ? page * limit : offset,
    };
  });

/**
 * Zod schema for validating a user table item.
 */
const zodUserTableItemSchema = z.object({
  companyName: z.string().nullish(),
  email: EmailSchema,
  firstName: z.string().nullish(),
  isActive: z.boolean(),
  lastActiveDatetime: z.iso.datetime().nullish(),
  lastName: z.string().nullish(),
  mobileAppVersion: z.string().nullish(),
  signedDeliveryNotesCount: z.number().int().nonnegative(),
  userId: z.uuid(),
});

/**
 * Zod schema for validating the analytics details API response for users.
 */
export const zodUserAnalyticsDetailsResponseSchema = z.object({
  data: z.array(zodUserTableItemSchema),
  hasNextPage: z.boolean(),
  totalCount: z.number().int().nonnegative(),
});
